import { UpperCasePipe } from '@angular/common';
import { Component, computed, inject, Injector, OnInit, Signal } from '@angular/core';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatChip } from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';
import { DatatableComponent } from '../../../shared/components/datatable/datatable.component';
import {
  ETableCrudScope,
  IDatatableColumn,
  IDatatableConfiguration,
} from '../../../shared/components/datatable/datatable.interface';
import { HttpUtilitiesService } from '../../../utilities/http-utilities.service';
import { BaseCrudPageComponent } from '../../app/base-crud-page.component';
import { IshikawaTemplateDialogComponent } from './ishikawa-template-dialog/ishikawa-template-dialog.component';
import {
  ISHIKAWA_DEFAULT_BONE_LABEL_PREFIX,
  ISHIKAWA_DEFAULT_TEMPLATE_LABEL_PREFIX,
} from './ishikawa-templates.constants';
import {
  EIshikawaTemplateDialogMode,
  IIshikawaTemplate,
  IIshikawaTemplateBone,
  IIshikawaTemplateBoneView,
  IIshikawaTemplateDialogData,
  IIshikawaTemplateRow,
} from './ishikawa-templates.interface';
import { IshikawaTemplatesService } from './ishikawa-templates.service';
import { IIshikawaTemplatesComponentState, IshikawaTemplatesStore } from './ishikawa-templates.store';

@Component({
  imports: [DatatableComponent, MatCard, MatCardContent, MatChip, MatIcon, TranslateModule, UpperCasePipe],
  providers: [HttpUtilitiesService, IshikawaTemplatesService, IshikawaTemplatesStore],
  selector: 'app-ishikawa-templates',
  styleUrl: './ishikawa-templates.component.scss',
  templateUrl: './ishikawa-templates.component.html',
})
export class IshikawaTemplatesComponent
  extends BaseCrudPageComponent<IIshikawaTemplate, IIshikawaTemplatesComponentState>
  implements OnInit
{
  private readonly injector = inject(Injector);

  public readonly datatableConfigurations: IDatatableConfiguration<IIshikawaTemplateRow> = {
    addButtonText: this.addText,
    addScope: ETableCrudScope.single,
    dataRefreshEvent: this.setPageToOneAndRefreshDatatable$,
    deleteScope: ETableCrudScope.single,
    duplicateScope: ETableCrudScope.single,
    editScope: ETableCrudScope.single,
    // Default templates are read-only: they can only be viewed and duplicated into an own template.
    specialRows: [
      {
        deleteCondition(): ETableCrudScope {
          return ETableCrudScope.none;
        },
        duplicateCondition(): ETableCrudScope {
          return ETableCrudScope.single;
        },
        editCondition(): ETableCrudScope {
          return ETableCrudScope.none;
        },
        requirement(this: DatatableComponent<IIshikawaTemplateRow>): boolean {
          return this.checkboxModel.selected.some(
            (rowId: number) => this.rowData().find((row: IIshikawaTemplateRow) => row.id === rowId)?.isDefault,
          );
        },
      },
    ],
  };

  public readonly datatableColumns: IDatatableColumn<IIshikawaTemplateRow>[] = [
    { id: 'id', isSortable: false },
    { id: 'name', isSortable: true },
    { id: 'bones', isSortable: false },
  ];

  /** List rows with default template and bone names translated and bones in fishbone order. */
  public readonly rows: Signal<IIshikawaTemplateRow[]> = computed(() =>
    this.store.data$().map((template: IIshikawaTemplate) => ({
      bones: this.getBoneViews(template),
      id: template.id,
      isDefault: template.isDefault,
      name: this.getTemplateName(template),
    })),
  );

  constructor(public readonly store: IshikawaTemplatesStore) {
    super(store, {
      ambiguousNumberContext: 'field.ambiguousNumberOfIshikawaTemplate',
      multiContext: 'field.ishikawaTemplates',
      nameProperty: 'name',
      singleContext: 'field.ishikawaTemplate',
    });
  }

  public ngOnInit(): void {
    this.subscriptions.push(
      this.datatableParams$.pipe(filter(Boolean)).subscribe((datatable) => {
        this.store.loadItems({
          fields: ['name', 'isDefault', 'bones.name', 'bones.isDefault', 'bones.icon', 'bones.color', 'bones.order'],
          join: ['bones'],
          limit: datatable.limit,
          page: datatable.page,
          sort: [{ active: 'isDefault', direction: 'desc' }, datatable.sort],
          ...(datatable.search ? { search: { searchedFields: ['name'], searchText: datatable.search } } : {}),
        });
      }),
    );
  }

  public initializeAdd(): void {
    this.setupForAdd();
    this.openDialog({ bones: [], id: null, mode: EIshikawaTemplateDialogMode.add, name: '' });
  }

  public initializeEdit(): void {
    const row: IIshikawaTemplateRow | undefined = this.getSelectedRow();

    if (row && !row.isDefault) {
      this.setupForEdit();
      this.openTemplate(row);
    }
  }

  /** Opens the add dialog filled with a copy of the selected template; default names arrive translated. */
  public initializeDuplicate(): void {
    const row: IIshikawaTemplateRow | undefined = this.getSelectedRow();

    if (!row) {
      return;
    }

    this.setupForAdd();
    this.openDialog({
      bones: row.bones.map(({ color, icon, name }: IIshikawaTemplateBoneView) => ({ color, icon, name })),
      id: null,
      mode: EIshikawaTemplateDialogMode.add,
      name: this.translate.instant('page.ishikawaTemplates.copyName', { name: row.name }),
    });
  }

  /** Name link of a row: default templates open read-only, own templates open for editing. */
  public openTemplate(row: IIshikawaTemplateRow): void {
    this.openDialog({
      bones: row.bones.map(({ color, icon, id, name }: IIshikawaTemplateBoneView) => ({ color, icon, id, name })),
      id: row.id,
      mode: row.isDefault ? EIshikawaTemplateDialogMode.view : EIshikawaTemplateDialogMode.edit,
      name: row.name,
    });
  }

  private openDialog(data: IIshikawaTemplateDialogData): void {
    this.matDialog.open(IshikawaTemplateDialogComponent, {
      ...IshikawaTemplateDialogComponent.dialogParameters,
      data,
      // The dialog saves through this page's store; the page closes it once the save succeeds.
      injector: this.injector,
    });
  }

  private getSelectedRow(): IIshikawaTemplateRow | undefined {
    const [selectedId] = this.selectedDatatableItems();

    return this.rows().find((row: IIshikawaTemplateRow) => row.id === selectedId);
  }

  private getTemplateName(template: IIshikawaTemplate): string {
    return template.isDefault
      ? this.translate.instant(`${ISHIKAWA_DEFAULT_TEMPLATE_LABEL_PREFIX}.${template.name}`)
      : template.name;
  }

  private getBoneViews(template: IIshikawaTemplate): IIshikawaTemplateBoneView[] {
    return [...(template.bones ?? [])]
      .sort((first: IIshikawaTemplateBone, second: IIshikawaTemplateBone) => first.order - second.order)
      .map((bone: IIshikawaTemplateBone) => ({
        color: bone.color,
        icon: bone.icon,
        id: bone.id,
        name: bone.isDefault ? this.translate.instant(`${ISHIKAWA_DEFAULT_BONE_LABEL_PREFIX}.${bone.name}`) : bone.name,
      }));
  }
}
